import {Action, Dispatch} from 'redux';
import {USER_ROLE} from '../../Config/appConfig';
import {Auth, Credentials, Email, Hmac, Option, UpdateUser, ResetPassword, Token, SecretDetails} from '../../Config/dataModel';
import util from '../../Util/util';
import authAPI from '../APIs/authAPI';
import userManagementAPI from '../APIs/userManagementAPI';
import {disconnect} from "../../Websocket/websocket";

const TYPES = {
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    ROBOT_LOGIN_SUCCESS: 'ROBOT_LOGIN_SUCCESS',
    TD_LOGIN_SUCCESS: 'TD_LOGIN_SUCCESS',
    LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
	FORGOT_PASSWORD_EMAIL_SUCCESS: 'FORGOT_PASSWORD_EMAIL_SUCCESS',
    UPDATE_USER_SUCCESS: 'UPDATE_USER_SUCCESS',
	RESET_PASSWORD_SUCCESS: 'RESET_PASSWORD_SUCCESS',
    VALIDATE_IDENTITY_SUCCESS: 'VALIDATE_IDENTITY_SUCCESS',
    RESET_SECRET_SUCCESS: 'RESET_SECRET_SUCCESS'
};

const login = (credentials: Credentials) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.login(credentials).then(res => {
            if (res.data.robot) dispatch(storeRobotLoginStatus(true));
            else if (res.data.td) dispatch(storeTdLoginStatus(true));
            else {
                const isInsurer = res.data.companyId.insurer;
                const auth = {
                    token: res.headers.authorization,
                    user: {
                        firstName: res.data.firstName,
                        lastName: res.data.lastName,
                        name: `${res.data.firstName} ${res.data.lastName}`,
                        userRole: isInsurer ? USER_ROLE.INSURER : USER_ROLE.SUBAGENT,
                        email: res.data.email,
                        companyCode: res.data.companyId.shortId,
                        company: res.data.companyId.displayName,
                        x500Name: res.data.companyId.x500Name,
                        isAdmin: res.data.admin,
                        subscribe: res.data.receiveEmailNotification
                    },
                    displayMaintenance: true
                };
                setAuth(auth);
                Promise.all([
                    fetchRegisteredCounterparties(isInsurer),
                    fetchAllCounterparties(isInsurer)
                ]).then(() => {
                    dispatch(storeLoginStatus(true));
                }).catch(error => {
                    throw(error);
                });
            }
        }).catch(error => {
            throw(error);
        });
    };
};

const logout = () => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.logout().then(res => {
            resetAuth();
            dispatch(storeLogoutStatus(false));
        }).catch(error => {
            resetAuth();
            throw(error);
        });
    };
};

const forgotPassword = (data: Email) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.forgotPassword(data).then(res => {
            dispatch(storeForgotPasswordEmailStatus(true));
        }).catch(error => {
            throw(error);
        });
    };
};

const resetPassword = (data: ResetPassword) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.resetPassword(data).then(res => {
            dispatch(storeResetPasswordStatus(true));
        }).catch(error => {
            throw(error);
        });
    };
};

const updateUser = (data: UpdateUser) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.updateUser(data).then(res => {
            let auth = getAuth();
            if (data.firstName && data.lastName) {
                auth.user.firstName = data.firstName;
                auth.user.lastName = data.lastName;
                auth.user.name = `${data.firstName} ${data.lastName}`;
                setAuth(auth);
            }
            if ('receiveEmailNotification' in data) {
                auth.user.subscribe = data.receiveEmailNotification;
                setAuth(auth);
            }
            dispatch(storeUpdateUserStatus(true));
        }).catch(error => {
            throw(error);
        });
    };
};

const fetchRegisteredCounterparties = (isInsurer: boolean) => {
    return new Promise((resolve, reject) => {
        const api = isInsurer ? userManagementAPI.registeredSubagents() : userManagementAPI.registeredInsurers();
        api.then(res => {
            let auth = getAuth();
            auth.registeredCounterparties = util.getParties(res.data);
            if (isInsurer) { auth.insurer = util.getCompany(getAuth().user.x500Name, getAuth().user.company); }
            setAuth(auth);
            resolve();
        }).catch(error => {
            reject(error);
        });
    });
};

const fetchAllCounterparties = (isInsurer: boolean) => {
    return new Promise((resolve, reject) => {
        const api = isInsurer ? userManagementAPI.allUserSubagents() : userManagementAPI.allUserInsurers();
        api.then(res => {
            let auth = getAuth();
            auth.allCounterparties = util.getParties(res.data);
            setAuth(auth);
            resolve();
        }).catch(error => {
            reject(error);
        });
    });
};

const validateIdentityNo = (hmac: Hmac) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.validateIdentityNo(hmac).then(res => {
            dispatch(storeValidationStatus(res.data));
        }).catch(error => {
            throw(error);
        });
    };
};

const resetSecret = (data: Token) => {
    return function (dispatch: Dispatch<Action>) {
        return authAPI.resetSecret(data).then(res => {
            dispatch(storeResetSecret(res.data));
        }).catch(error => {
            throw(error);
        });
    };
};

const hideMaintenance = () => {
    return function (dispatch: Dispatch<Action>) {
        let auth = getAuth();
        auth.displayMaintenance = false;
        setAuth(auth);
    };
};

const storeLoginStatus = (success: boolean) => {
    return {
        type: TYPES.LOGIN_SUCCESS,
        loggedIn: success
    };
};

const storeRobotLoginStatus = (success: boolean) => {
    return {
        type: TYPES.ROBOT_LOGIN_SUCCESS,
        robotLoggedIn: success
    };
};

const storeTdLoginStatus = (success: boolean) => {
    return {
        type: TYPES.TD_LOGIN_SUCCESS,
        tdLoggedIn: success
    };
};

const storeLogoutStatus = (success: boolean) => {
    return {
        type: TYPES.LOGOUT_SUCCESS,
        loggedIn: success
    };
};

const storeForgotPasswordEmailStatus = (success: boolean) => {
    return {
        type: TYPES.FORGOT_PASSWORD_EMAIL_SUCCESS,
        forgotPasswordEmailStatus: success
    };
};

const storeResetPasswordStatus = (success: boolean) => {
    return {
        type: TYPES.RESET_PASSWORD_SUCCESS,
        resetPasswordStatus: success
    };
};

const storeUpdateUserStatus = (success: boolean) => {
    return {
        type: TYPES.UPDATE_USER_SUCCESS,
        updateUserStatus: success
    };
};

const storeValidationStatus = (success: boolean) => {
    return {
        type: TYPES.VALIDATE_IDENTITY_SUCCESS,
        validationStatus: success
    };
};

const storeResetSecret = (data: SecretDetails) => {
    return {
        type: TYPES.RESET_SECRET_SUCCESS,
        secretDetails: data
    };
};

const setAuth = (auth: Auth) => {
    localStorage.setItem('auth', JSON.stringify(auth));
};

const setSelectedInsurer = (insurer: Option) => {
    return function (dispatch: Dispatch<Action>) {
        let auth = getAuth();
        auth.insurer = insurer;
        setAuth(auth);
    };
};

const getAuth = () => {
    return JSON.parse(localStorage.getItem('auth'));
};

const getInsurerId = () => {
    return isInsurer() ?
        util.getInsurerId(getAuth().user.x500Name) :
        util.getInsurerId(getAuth().insurer.value);
};

const getSubscribeStatus = () => {
    return getAuth().user.subscribe;
};

const resetAuth = () => {
    localStorage.removeItem('auth');
    disconnect();
};

const isInsurer = () => {
    return getAuth().user.userRole === USER_ROLE.INSURER;
};

export {
    TYPES,
    login,
    logout,
    forgotPassword,
    resetPassword,
    updateUser,
    validateIdentityNo,
    resetSecret,
    setSelectedInsurer,
    hideMaintenance,
    getAuth,
    getInsurerId,
    getSubscribeStatus,
    resetAuth,
    isInsurer
};



// WEBPACK FOOTER //
// ./src/ReduxRequests/Actions/authAction.ts